import {
  ExtranetCustomerUserProfessionalRoleDto,
  ExtranetCustomerUserRoleDto,
} from '@pharmac-extranet/extranet-api'
import { ExtranetContainer, useBreakpoint, useLanguage } from '@pharmac-extranet/shared'
import {
  PharmacFieldset,
  PharmacFieldsetLegend,
  PharmacH6,
  PharmacSelect,
  PharmacText,
} from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'

type SelectedCustomerInfo = {
  customerName: string
  customerId: number
  roles: ExtranetCustomerUserRoleDto[]
  professionalRoles?: ExtranetCustomerUserProfessionalRoleDto[]
}

export const UserCustomerRoles = () => {
  const { key } = useLanguage()
  const { maxMobile, minDesktop, maxTablet } = useBreakpoint()

  const { user } = useAuthenticationContext()
  const { userCustomers, customer } = useCustomerContext()

  const [selectedCustomerId, setSelectedCustomerId] = useState<undefined | number>(undefined)
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomerInfo | undefined>(
    undefined
  )

  useEffect(() => {
    if (customer) {
      setSelectedCustomerId(customer.id)
    }
  }, [customer])

  const { data: customerRoles, isLoading } = useQuery({
    queryKey: ['__userViewUserCustomerRoles', { userId: user?.id, customerId: selectedCustomerId }],
    queryFn: async () => await api.customers.getMyCustomerUserRoles(selectedCustomerId as number),
    enabled: !!user?.id && !!selectedCustomerId,
  })

  useEffect(() => {
    if (!!selectedCustomerId && customerRoles && !isLoading) {
      const userCustomer = userCustomers.find((a) => a.id === selectedCustomerId)

      const roles = customerRoles.roles

      if (!userCustomer || roles.length === 0) {
        return
      }

      setSelectedCustomer(() => ({
        customerId: userCustomer.id,
        customerName: userCustomer.name,
        professionalRoles: user?.professionalRoles,
        roles,
      }))
    }
  }, [selectedCustomerId, customerRoles, isLoading])

  if (!selectedCustomer) {
    return null
  }

  return (
    <PharmacFieldset
      marginMediumTop={!maxMobile}
      marginLargeTop={maxMobile}
      paddingMediumBottom
      style={
        maxMobile
          ? {
              paddingTop: 'var(--spacing-medium)',
              paddingBottom: 'var(--spacing-medium)',
              paddingLeft: 0,
            }
          : undefined
      }
    >
      <PharmacFieldsetLegend marginSmallLeft={maxMobile}>{t('common:roles')}</PharmacFieldsetLegend>
      <ExtranetContainer
        flexRow={!maxTablet}
        flexColumn={maxTablet}
        alignCenter={!maxTablet}
        justifyCenter={!maxTablet}
        full
      >
        <ExtranetContainer
          half={!maxTablet}
          full={maxTablet}
          marginMediumLeft={maxTablet}
          marginMediumBottom
        >
          <div style={{ width: '100%', maxWidth: !maxMobile ? 325 : undefined }}>
            <PharmacSelect
              value={selectedCustomerId}
              onChange={(value: string) => setSelectedCustomerId(+value)}
            >
              {userCustomers.map((uc) => (
                <option
                  key={uc.id}
                  value={uc.id}
                >
                  {uc.name}
                </option>
              ))}
            </PharmacSelect>
          </div>
        </ExtranetContainer>
        <ExtranetContainer
          half
          flexRow={minDesktop}
          flexColumn={!minDesktop}
        >
          <ExtranetContainer
            flexColumn
            half={!maxTablet}
            full={maxTablet}
            paddingSmallTop={maxTablet}
            paddingLargeLeft={minDesktop}
            paddingMediumLeft={maxTablet}
            paddingMediumBottom={!minDesktop}
          >
            <PharmacH6 paddingMediumBottom>{t('common:professionalRoles')}</PharmacH6>
            {selectedCustomer.professionalRoles?.length !== 0 ? (
              <>
                {selectedCustomer.professionalRoles?.map((role) => (
                  <PharmacText
                    marginSmallBottom
                    style={{ fontSize: 'var(--typo-h6-size)' }}
                  >
                    {key(role?.displayName)}
                  </PharmacText>
                ))}
              </>
            ) : (
              <PharmacText>-</PharmacText>
            )}
          </ExtranetContainer>
          <ExtranetContainer
            flexColumn
            half={!maxTablet}
            full={maxTablet}
            paddingSmallTop={maxTablet}
            paddingLargeLeft={minDesktop}
            paddingMediumLeft={maxTablet}
          >
            <PharmacH6 paddingMediumBottom>{t('extranet:user.userRoles')}</PharmacH6>
            {selectedCustomer.roles.map((a) => (
              <PharmacText
                marginSmallBottom
                key={a.role}
                style={{ fontSize: 'var(--typo-h6-size)' }}
              >
                {t(`customer.roles.${a.role}`)}
              </PharmacText>
            ))}
          </ExtranetContainer>
        </ExtranetContainer>
      </ExtranetContainer>
    </PharmacFieldset>
  )
}
